import type { ITemplateRepository, TemplateListParams } from "@/application/ports/repositories";
import type { BulkResult } from "@/domain/types/bulk";
import type {
  CatalogResponse,
  TemplateListFilters,
  TemplateListResponse,
  TemplateSort,
  DuplicateTemplateRequest,
  PaginatedTemplates,
  TemplateCreateRequest,
  TemplateDetail,
  TemplateFilterState,
  TemplateImagePreviewResponse,
  TemplateImageUploadRequest,
  TemplateImageUploadResponse,
  TemplateUpdateRequest,
} from "@/domain/template";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlob } from "../unwrap";

const asList = <T>(value: T | T[] | undefined): T[] | undefined =>
  value === undefined ? undefined : Array.isArray(value) ? value : [value];

const many = <T>(values: readonly T[] | undefined) =>
  values && values.length > 0 ? [...values] : undefined;

const toFilterQuery = (filters: TemplateListFilters) => ({
  search: filters.search || undefined,
  type: many(filters.type),
  category: many(filters.category),
  language: many(filters.language),
  industry: many(filters.industry),
  origin: many(filters.origin),
  status: many(filters.status),
});

export class TemplateRepository implements ITemplateRepository {
  async listTemplates(params: TemplateListParams, signal?: AbortSignal) {
    const { sort, limit, offset, ...filters } = params;
    const response = await unwrap(
      sdk.listTemplates({
        query: {
          ...toFilterQuery(filters),
          sort:
            sort && sort.length > 0
              ? sort.map((entry: TemplateSort) => `${entry.key}:${entry.direction}`).join(",")
              : undefined,
          limit,
          offset,
        },
        signal,
      }),
    );
    return { templates: response.items, total_count: response.total_count } as TemplateListResponse;
  }

  async getTemplates(
    filters?: TemplateFilterState,
    signal?: AbortSignal,
  ): Promise<PaginatedTemplates> {
    const { type, category, language, industry, ...rest } = filters ?? {};
    const { items, ...page } = await unwrap(
      sdk.listTemplates({
        query: {
          ...rest,
          type: asList(type),
          category: asList(category),
          language: asList(language),
          industry: asList(industry),
        },
        signal,
      }),
    );
    return { templates: items, ...page };
  }

  async getTemplate(uuid: string, signal?: AbortSignal): Promise<TemplateDetail> {
    return unwrap(sdk.getTemplate({ path: { template_uuid: uuid }, signal }));
  }

  async createTemplate(data: TemplateCreateRequest): Promise<TemplateDetail> {
    return unwrap(sdk.createTemplate({ body: data }));
  }

  async updateTemplate(uuid: string, data: TemplateUpdateRequest): Promise<TemplateDetail> {
    return unwrap(sdk.updateTemplate({ path: { template_uuid: uuid }, body: data }));
  }

  async duplicateTemplate(uuid: string, data: DuplicateTemplateRequest): Promise<TemplateDetail> {
    return unwrap(sdk.duplicateTemplate({ path: { template_uuid: uuid }, body: data }));
  }

  async deleteTemplate(uuid: string): Promise<void> {
    await unwrap(sdk.deleteTemplate({ path: { template_uuid: uuid } }));
  }

  async bulkUpdateTemplates(
    uuids: readonly string[],
    data: TemplateUpdateRequest,
  ): Promise<BulkResult> {
    return unwrap(sdk.bulkUpdateTemplates({ body: { template_uuids: [...uuids], data } }));
  }

  async bulkDuplicateTemplates(
    uuids: readonly string[],
    data: DuplicateTemplateRequest,
  ): Promise<BulkResult> {
    return unwrap(sdk.bulkDuplicateTemplates({ body: { template_uuids: [...uuids], data } }));
  }

  async bulkDeleteTemplates(uuids: readonly string[]): Promise<BulkResult> {
    return unwrap(sdk.bulkDeleteTemplates({ body: { template_uuids: [...uuids] } }));
  }

  async getCatalog(signal?: AbortSignal): Promise<CatalogResponse> {
    return unwrap(sdk.getCatalog({ signal }));
  }

  async previewTemplate(uuid: string): Promise<Blob> {
    return unwrapBlob(sdk.previewTemplate, { path: { template_uuid: uuid } });
  }

  async uploadTemplateImage(
    uuid: string,
    data: TemplateImageUploadRequest,
  ): Promise<TemplateImageUploadResponse> {
    const response = await unwrap(
      sdk.uploadTemplateImage({
        path: { template_uuid: uuid },
        body: { data_uri: data.dataUri },
      }),
    );
    return { src: response.src, signedUrl: response.signed_url };
  }

  async getTemplateImagePreview(
    uuid: string,
    src: string,
    signal?: AbortSignal,
  ): Promise<TemplateImagePreviewResponse> {
    const response = await unwrap(
      sdk.getTemplateImagePreview({
        path: { template_uuid: uuid },
        body: { src },
        signal,
      }),
    );
    return { signedUrl: response.signed_url };
  }
}
